/**
 * Reusable Typography components for consistent text styling
 *
 * Usage:
 *   <PageTitle>Customers</PageTitle>
 *   <SectionTitle>Active Leads</SectionTitle>
 *   <Label>Full Name</Label>
 *   <BodyText>Some description here</BodyText>
 *   <SubText>Last updated 2 hours ago</SubText>
 *   <Caption>Footnote text</Caption>
 */

export function PageTitle({ children, className = '', as: Tag = 'h1', ...props }) {
  return (
    <Tag
      className={`text-[19px] leading-7 font-bold text-brand-dark tracking-tight font-heading ${className}`}
      {...props}
    >
      {children}
    </Tag>
  );
}

export function SectionTitle({ children, className = '', as: Tag = 'h2', ...props }) {
  return (
    <Tag
      className={`text-[15px] leading-[22px] font-semibold text-brand-dark ${className}`}
      {...props}
    >
      {children}
    </Tag>
  );
}

export function SectionSubtitle({ children, className = '', as: Tag = 'h3', ...props }) {
  return (
    <Tag
      className={`text-[11x] leading-5 font-medium text-slate-700 ${className}`}
      {...props}
    >
      {children}
    </Tag>
  );
}

export function Label({ children, htmlFor, required, className = '', ...props }) {
  return (
    <label
      htmlFor={htmlFor}
      className={`block text-[13px] font-medium text-slate-500 leading-tight tracking-normal ${className}`}
      {...props}
    >
      {children}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  );
}

export function BodyText({ children, className = '', as: Tag = 'p', ...props }) {
  return (
    <Tag
      className={`text-[13px] leading-5 font-medium text-slate-700 ${className}`}
      {...props}
    >
      {children}
    </Tag>
  );
}

export function SubText({ children, className = '', as: Tag = 'p', ...props }) {
  return (
    <Tag
      className={`text-[11px] leading-4 font-medium text-slate-500 ${className}`}
      {...props}
    >
      {children}
    </Tag>
  );
}

export function Caption({ children, className = '', as: Tag = 'span', ...props }) {
  return (
    <Tag
      className={`text-[12px] leading-3 font-normal text-slate-400 tracking-wider ${className}`}
      {...props}
    >
      {children}
    </Tag>
  );
}

export function ErrorText({ children, className = '', ...props }) {
  return (
    <p className={`text-[12px] font-medium text-red-500 ${className}`} {...props}>
      {children}
    </p>
  );
}

export function HighlightText({ children, className = '', ...props }) {
  return (
    <span className={`text-[13px] font-bold text-brand-blue ${className}`} {...props}>
      {children}
    </span>
  );
}
